import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { clubService, membershipService, eventService, forumService } from '../services/apiClient';
import Navbar from '../components/Navbar';
import { Users, Calendar, MessageSquare, Check, X, Plus, Edit, Trash2, Eye } from 'lucide-react';

const AdminDashboard = () => {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const [managedClubs, setManagedClubs] = useState([]);
    const [selectedClub, setSelectedClub] = useState(null);
    const [stats, setStats] = useState(null);
    const [pendingRequests, setPendingRequests] = useState([]);
    const [events, setEvents] = useState([]);
    const [forumPosts, setForumPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');

    useEffect(() => {
        if (user?.role === 'Student') {
            navigate('/dashboard');
            return;
        }
        fetchAdminClubs();
    }, [user]);

    const fetchAdminClubs = async () => {
        try {
            const res = await clubService.getAllClubs();
            const filtered = res.data.filter(c =>
                user.role === 'SuperAdmin' || c.admin?._id === user._id || c.admin === user._id
            );
            setManagedClubs(filtered);
            if (filtered.length > 0) {
                setSelectedClub(filtered[0]);
                fetchClubData(filtered[0]._id);
            } else {
                setLoading(false);
            }
        } catch (error) {
            console.error('Error fetching admin clubs:', error);
            setLoading(false);
        }
    };

    const fetchClubData = async (clubId) => {
        setLoading(true);
        try {
            const [statsRes, reqRes, eventRes, forumRes] = await Promise.all([
                clubService.getClubStats(clubId),
                membershipService.getPendingMemberships({ clubId }),
                eventService.getEventsByClub(clubId),
                forumService.getPostsByClub(clubId)
            ]);
            setStats(statsRes.data);
            setPendingRequests(reqRes.data);
            setEvents(eventRes.data);
            setForumPosts(forumRes.data);
        } catch (error) {
            console.error('Error fetching club data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedClub) {
            fetchClubData(selectedClub._id);
        }
    }, [selectedClub]);

    const handleApprove = async (id) => {
        try {
            await membershipService.approveMembership(id);
            fetchClubData(selectedClub._id);
        } catch (error) {
            alert('Approve failed');
        }
    };

    const handleReject = async (id) => {
        const reason = prompt('Rejection reason:');
        if (!reason) return;
        try {
            await membershipService.rejectMembership(id, { reason });
            fetchClubData(selectedClub._id);
        } catch (error) {
            alert('Reject failed');
        }
    };

    const handleDeleteEvent = async (id) => {
        if (!window.confirm('Delete this event?')) return;
        try {
            await eventService.deleteEvent(id);
            fetchClubData(selectedClub._id);
        } catch (error) {
            alert('Delete failed');
        }
    };

    if (loading && !selectedClub) return <div className="p-8 text-center">Loading Admin Panel...</div>;
    if (managedClubs.length === 0) return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            <div className="p-20 text-center">
                <h2 className="text-2xl font-bold text-gray-400">You don't manage any clubs yet.</h2>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            <Navbar />

            <div className="max-w-7xl mx-auto px-4 py-8">
                <header className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 leading-tight">Club Management</h1>
                        <p className="text-gray-500 font-medium">Welcome, Admin <span className="text-purple-600 font-bold">{user.name}</span></p>
                    </div>

                    <div className="flex items-center gap-3">
                        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest">Selected Club:</label>
                        <select
                            className="bg-white border border-gray-200 px-4 py-2.5 rounded-xl text-sm font-bold text-gray-800 shadow-sm focus:ring-2 focus:ring-purple-500"
                            onChange={(e) => {
                                const club = managedClubs.find(c => c._id === e.target.value);
                                setSelectedClub(club);
                            }}
                            value={selectedClub?._id}
                        >
                            {managedClubs.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                        </select>
                    </div>
                </header>

                {/* Quick Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {[
                        { label: 'Members', value: stats?.totalMembers, icon: <Users />, color: 'purple' },
                        { label: 'Events', value: stats?.totalEvents, icon: <Calendar />, color: 'blue' },
                        { label: 'Pending Req', value: stats?.pendingMemberships, icon: <Check />, color: 'orange' },
                        { label: 'Forum Doubts', value: stats?.totalDoubts, icon: <MessageSquare />, color: 'green' }
                    ].map((s, i) => (
                        <div key={i} className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4">
                            <div className={`p-3 bg-${s.color}-100 text-${s.color}-600 rounded-xl`}>{React.cloneElement(s.icon, { className: 'w-5 h-5' })}</div>
                            <div>
                                <p className="text-gray-400 text-[10px] font-bold uppercase tracking-wider">{s.label}</p>
                                <p className="text-2xl font-black text-gray-900">{s.value}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
                    <div className="flex border-b border-gray-50 p-2 bg-gray-50/50">
                        {['overview', 'memberships', 'events', 'forum'].map(tab => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-6 py-3 rounded-2xl text-xs font-bold uppercase tracking-widest transition ${activeTab === tab ? 'bg-white text-purple-600 shadow-md' : 'text-gray-400 hover:text-gray-600'
                                    }`}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>

                    <div className="p-8">
                        {activeTab === 'overview' && (
                            <div className="space-y-8">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="bg-purple-50 p-6 rounded-2xl border border-purple-100">
                                        <h3 className="font-bold text-purple-900 mb-2">Club Mission</h3>
                                        <p className="text-purple-700 text-sm leading-relaxed">{selectedClub?.mission || 'No mission defined.'}</p>
                                    </div>
                                    <div className="bg-indigo-50 p-6 rounded-2xl border border-indigo-100">
                                        <h3 className="font-bold text-indigo-900 mb-2">Admin Notice</h3>
                                        <p className="text-indigo-700 text-sm leading-relaxed italic">"Always verify membership applications before approval. Active participation is key!"</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'memberships' && (
                            <div className="space-y-6">
                                <h3 className="text-xl font-bold text-gray-800">Pending Requests</h3>
                                {pendingRequests.length === 0 ? <p className="text-gray-400 italic">No pending requests.</p> : (
                                    <div className="grid grid-cols-1 gap-4">
                                        {pendingRequests.map(req => (
                                            <div key={req._id} className="bg-white border border-gray-100 p-5 rounded-2xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4 hover:shadow-md transition">
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center font-bold text-gray-500">
                                                        {req.user?.name.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <p className="font-bold text-gray-900">{req.user?.name}</p>
                                                        <p className="text-xs text-gray-500">ID: {req.user?.rollNumber || 'N/A'}</p>
                                                    </div>
                                                </div>
                                                <div className="flex gap-2">
                                                    <button onClick={() => handleApprove(req._id)} className="bg-green-600 text-white px-4 py-2 rounded-xl text-xs font-bold hover:bg-green-700 transition">Approve</button>
                                                    <button onClick={() => handleReject(req._id)} className="bg-red-50 text-red-600 px-4 py-2 rounded-xl text-xs font-bold hover:bg-red-100 transition">Reject</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'events' && (
                            <div className="space-y-6">
                                <div className="flex justify-between items-center">
                                    <h3 className="text-xl font-bold text-gray-800">Managed Events</h3>
                                    <button onClick={() => navigate(`/club/${selectedClub._id}`)} className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-xl text-xs font-bold hover:bg-purple-700 transition">
                                        <Plus className="w-4 h-4" /> Create New
                                    </button>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left">
                                        <thead>
                                            <tr className="text-gray-400 text-[10px] font-bold uppercase tracking-tighter border-b border-gray-50">
                                                <th className="pb-4">Name</th>
                                                <th className="pb-4">Date</th>
                                                <th className="pb-4">Registrations</th>
                                                <th className="pb-4 text-right">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                            {events.map(ev => (
                                                <tr key={ev._id} className="group hover:bg-gray-50 transition">
                                                    <td className="py-4 font-bold text-gray-800 text-sm">{ev.name}</td>
                                                    <td className="py-4 text-xs text-gray-500">{new Date(ev.date).toLocaleDateString()}</td>
                                                    <td className="py-4 text-xs font-bold text-blue-600">{ev.registrationCount || 0}</td>
                                                    <td className="py-4 text-right flex justify-end gap-2">
                                                        <button className="p-2 text-gray-400 hover:text-purple-600"><Edit className="w-4 h-4" /></button>
                                                        <button onClick={() => handleDeleteEvent(ev._id)} className="p-2 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {activeTab === 'forum' && (
                            <div className="space-y-6">
                                <h3 className="text-xl font-bold text-gray-800">Forum Queries</h3>
                                <div className="grid grid-cols-1 gap-4">
                                    {forumPosts.map(post => (
                                        <div key={post._id} className="p-5 border border-gray-50 rounded-2xl bg-gray-50/50 hover:bg-white transition group border hover:border-purple-100">
                                            <div className="flex justify-between items-start mb-4">
                                                <div>
                                                    <h4 className="font-bold text-gray-900">{post.title}</h4>
                                                    <p className="text-xs text-gray-500 mt-1">By {post.author?.name} • {post.replies?.length || 0} replies</p>
                                                </div>
                                                <button onClick={() => navigate('/forum')} className="text-purple-600 p-2 bg-white rounded-xl shadow-sm opacity-0 group-hover:opacity-100 transition">
                                                    <MessageSquare className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <p className="text-sm text-gray-600 line-clamp-2">{post.description}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
